import {
  CheckCircle,
  MinusCircle,
  Pause as PauseIcon,
  RefreshCircle,
  SendDiagonal,
  UserBadgeCheck,
  XmarkCircle,
} from 'iconoir-react'
import { capitalize, startCase } from 'lodash'
import { ReactElement } from 'react'
import { LazyResourceProvider } from '../shared/LazyResourceProvider'
import { ActivityCard } from './ActivityCard'
import { KeyValueDrawer } from './KeyValueDrawer'
import { Badge } from 'app/components/ui/badge'
import type { BadgeColor } from 'app/components/ui/badge'
import type { PlanLine, PlanWithLines } from 'app/types/treatment'
import { formatCurrency } from 'lib/formatCurrency'

type Props = {
  presented: PlanWithLines
  isFirst?: boolean
  isLast?: boolean
  alwaysOpen?: boolean
}

const ICON = `h-5 w-5 rounded-md p-1 text-on-color`
const NEUTRAL_ICON = `${ICON} bg-fill-neutral`

/** `status` is the PMS's own word and is often absent; `state` only says OPEN or CLOSED. */
function planIcon(status: string | undefined, closed: boolean): ReactElement {
  switch (status?.toLowerCase()) {
    case `proposed`:
    case `planned`:
      return <SendDiagonal className={`${ICON} bg-blue-600`} />
    case `completed`:
      return <CheckCircle className={`${ICON} bg-blue-600`} />
    case `accepted`:
      return <UserBadgeCheck className={`${ICON} bg-success-default`} />
    case `on hold`:
      return <PauseIcon className={NEUTRAL_ICON} />
    case `inactive`:
      return <MinusCircle className={NEUTRAL_ICON} />
    case `declined`:
    case `rejected`:
      return <XmarkCircle className={`${ICON} bg-red-600`} />
    default:
      return closed ? <MinusCircle className={NEUTRAL_ICON} /> : <RefreshCircle className={`${ICON} bg-blue-600`} />
  }
}

function statusColor(status: string): BadgeColor {
  switch (status.toLowerCase()) {
    case `accepted`:
      return `green`
    case `declined`:
    case `rejected`:
      return `red`
    case `on hold`:
    case `inactive`:
      return `gray`
    default:
      return `blue`
  }
}

const LINE_STATE_COLOR: Record<string, BadgeColor> = { COMPLETED: `green`, DELETED: `red` }

function LineRow({ line }: { line: PlanLine }): ReactElement {
  const { procedure, fee } = line
  const state = (procedure.state ?? ``).toUpperCase()

  return (
    <li className="flex flex-col gap-y-0.5 border-b border-subtle py-2 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex items-baseline justify-between gap-x-3">
        <span className="min-w-0 truncate text-ui-sm text-strong">
          {procedure.procedureCode || `No code`}
          {procedure.tooth ? <span className="text-body-xs text-subtle">{` tooth ${procedure.tooth}`}</span> : null}
        </span>
        <span className="shrink-0 text-ui-sm font-semibold text-strong">{formatCurrency(fee, 2)}</span>
      </div>
      <div className="flex items-center justify-between gap-x-3 text-body-xs text-subtle">
        <span className="min-w-0 truncate">{procedure.description?.trim() || `No description`}</span>
        {state && <Badge color={LINE_STATE_COLOR[state] ?? `gray`} label={capitalize(state)} />}
      </div>
      {procedure.providerId && (
        <div className="text-body-xs text-subtle">
          <LazyResourceProvider locationId={procedure.locationId} resourceName={procedure.providerId} showSpinner />
        </div>
      )}
    </li>
  )
}

/**
 * A treatment plan in the contact's record feed: the plan as a whole up top,
 * and each procedure line on it once opened.
 */
export function TreatmentPlanCard({ presented, isFirst, isLast, alwaysOpen }: Props) {
  const { plan, lines, total, count } = presented
  const closed = (plan.state ?? ``).toUpperCase() === `CLOSED`

  const summary = (
    <KeyValueDrawer
      map={{
        Total: formatCurrency(total, 2),
        Procedures: String(count),
      }}
    />
  )

  return (
    <ActivityCard
      title={<span className="text-ui-sm text-strong">{plan.displayName?.trim() || `Treatment plan`}</span>}
      icon={planIcon(plan.status ?? undefined, closed)}
      activityDate={plan.planCreateDate}
      activityDateOptions={{ dateOnly: true }}
      tags={
        <>
          {plan.status && <Badge color={statusColor(plan.status)} label={startCase(plan.status)} />}
          {closed && <Badge color="gray" label="Closed" />}
        </>
      }
      preview={
        <div className="w-full rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          {summary}
        </div>
      }
      drawer={
        <div className="w-full rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          {summary}
          {lines.length > 0 ? (
            <ul className="mt-3 flex flex-col border-t border-subtle pt-3">
              {lines.map((line, index) => (
                <LineRow key={line.procedure.id ?? index} line={line} />
              ))}
            </ul>
          ) : (
            <p className="mt-3 border-t border-subtle pt-3 text-subtle">No procedures on this plan.</p>
          )}
        </div>
      }
      isFirst={isFirst}
      isLast={isLast}
      alwaysOpen={alwaysOpen}
    />
  )
}
